import { ChevronLeft, ChevronRight } from 'lucide-react';
import { orgMonth } from '@/lib/today';
import * as React from 'react';

import {
  Alert, AlertDescription, Button, Card, CardContent, Skeleton, Tooltip, TooltipContent,
  TooltipProvider, TooltipTrigger,
} from '@/components/cargofl-ui';
import { PageContainer } from '@/components/shell/page-container';
import { PageHeader } from '@/components/shell/page-header';
import { usePeriodShortcuts } from '@/lib/shortcuts';
import {
  ABSENCE_TYPES, TYPE_COLOR, TYPE_LABEL, useAbsenceOverview,
  type CalendarRow, type DayMark,
} from '@/lib/absence-overview-api';

/**
 * Who is away, as a month grid: one row per person, one column per day.
 *
 * This is a planning surface, so it shows PENDING requests as well as approved
 * ones, drawn hollow rather than filled. A calendar that only shows approved
 * leave is how two people book the same week off and find out when the second
 * one is decided.
 */
export function AbsenceCalendarPage() {
  const [month, setMonth] = React.useState(() => orgMonth());

  // Left/right or j/k to move a period, t for today. See lib/shortcuts.
  usePeriodShortcuts({
    previous: () => setMonth(shiftMonth(month, -1)),
    next: () => setMonth(shiftMonth(month, 1)),
    today: () => setMonth(orgMonth()),
  });
  const { data, isPending, isError, error } = useAbsenceOverview(month);

  const days = data ? Array.from({ length: data.daysInMonth }, (_, i) => i + 1) : [];
  // Only people with something to show. An 80-row grid of empty cells buries the
  // handful of rows that carry information.
  const rows = data?.rows.filter((row) => hasAnything(row)) ?? [];

  return (
    <PageContainer>
      <PageHeader
        title="Absence calendar"
        description="Who is away this month, and who has asked to be."
      />

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="icon" aria-label="Previous month" title="Previous month (← or j)"
          onClick={() => setMonth(shiftMonth(month, -1))}>
          <ChevronLeft className="size-4" />
        </Button>
        <span className="ts-num min-w-[9rem] text-center text-sm font-semibold">
          {monthLabel(month)}
        </span>
        <Button variant="outline" size="icon" aria-label="Next month" title="Next month (→ or k)"
          onClick={() => setMonth(shiftMonth(month, 1))}>
          <ChevronRight className="size-4" />
        </Button>
        <Button hint={"Jump to the current month"} variant="outline" onClick={() => setMonth(orgMonth())}>
          This month
        </Button>
      </div>

      <Legend />

      {isError && (
        <Alert className="border-destructive/30">
          <AlertDescription className="text-sm">
            {error instanceof Error ? error.message : 'Could not load that month.'}
          </AlertDescription>
        </Alert>
      )}

      {isPending && <Skeleton className="h-96 w-full" />}

      {data && (
        <Card>
          <CardContent className="p-0">
            {/* The grid is wider than any screen at 31 columns, so it scrolls in its
                own container with the name column pinned - scrolling a name out of
                view leaves a row of coloured squares belonging to nobody. */}
            <div className="overflow-x-auto">
              <TooltipProvider delayDuration={150}>
                <table className="w-full border-separate border-spacing-0 text-sm">
                  <thead>
                    <tr>
                      <th className="sticky left-0 z-20 border-b bg-card px-3 py-2 text-left text-xs font-semibold">
                        Person
                      </th>
                      {days.map((day) => (
                        <th
                          key={day}
                          className={`w-7 border-b px-0 py-2 text-center text-[10px] font-semibold ${
                            isWeekendColumn(data.firstWeekday, day)
                              ? 'bg-muted/40 text-muted-foreground'
                              : 'text-muted-foreground'
                          }`}
                        >
                          <div>{weekdayInitial(data.firstWeekday, day)}</div>
                          <div className="ts-num text-[11px] text-foreground">{day}</div>
                        </th>
                      ))}
                      <th className="border-b px-2 py-2 text-right text-xs font-semibold">Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.employeeId} className="group">
                        <td className="sticky left-0 z-10 whitespace-nowrap border-b bg-card px-3 py-1.5 text-xs font-medium group-hover:bg-muted/50">
                          {row.name}
                        </td>
                        {days.map((day) => (
                          <td key={day} className="border-b p-0 align-middle">
                            <DayCell mark={row.days[day]} name={row.name} day={day} month={month} />
                          </td>
                        ))}
                        <td className="ts-num border-b px-2 py-1.5 text-right text-xs font-semibold">
                          {row.totalDays > 0 ? row.totalDays : ''}
                        </td>
                      </tr>
                    ))}
                    {rows.length === 0 && (
                      <tr>
                        <td
                          colSpan={days.length + 2}
                          className="py-10 text-center text-muted-foreground"
                        >
                          Nobody is away this month.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </TooltipProvider>
            </div>
          </CardContent>
        </Card>
      )}
    </PageContainer>
  );
}

/**
 * One day for one person.
 *
 * Leave is a filled block in its type's colour when approved and a hollow one
 * when still pending, so the two are told apart without reading a legend or
 * relying on colour alone. Public holidays are a neutral tint - they are not
 * leave, and colouring them like it would make a company shutdown look like
 * everybody spending entitlement.
 */
function DayCell({
  mark,
  name,
  day,
  month,
}: {
  mark: DayMark | undefined;
  name: string;
  day: number;
  month: string;
}) {
  if (!mark || mark.kind === 'off') {
    return (
      <div
        className={`mx-auto h-5 w-5 rounded-[3px] ${
          mark?.kind === 'off' ? 'bg-muted/60' : ''
        }`}
      />
    );
  }

  if (mark.kind === 'holiday') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="mx-auto flex h-5 w-5 items-center justify-center rounded-[3px] border border-dashed border-muted-foreground/40 bg-muted/40 text-[9px] font-bold text-muted-foreground">
            H
          </div>
        </TooltipTrigger>
        <TooltipContent>
          {mark.name}
          {mark.halfDay ? ' (half day)' : ''}
        </TooltipContent>
      </Tooltip>
    );
  }

  const color = TYPE_COLOR[mark.type] ?? TYPE_COLOR.OTHER!;
  const approved = mark.status === 'APPROVED';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className="mx-auto h-5 w-5 rounded-[3px] border-2"
          style={{
            borderColor: color,
            backgroundColor: approved ? color : 'transparent',
            // A half day reads as half the block filled, which is the one thing a
            // number in a 20px cell cannot say.
            backgroundImage: mark.halfDay
              ? `linear-gradient(135deg, ${color} 0 50%, transparent 50% 100%)`
              : undefined,
          }}
        />
      </TooltipTrigger>
      <TooltipContent>
        <div className="font-medium">{name}</div>
        <div>
          {TYPE_LABEL[mark.type] ?? mark.type}
          {mark.halfDay ? ' · half day' : ''} · {approved ? 'approved' : 'awaiting approval'}
        </div>
        <div className="ts-num opacity-70">{`${day} ${monthLabel(month)}`}</div>
      </TooltipContent>
    </Tooltip>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
      {ABSENCE_TYPES.map((type) => (
        <span key={type} className="flex items-center gap-1.5">
          <span
            className="size-3 rounded-[3px]"
            style={{ backgroundColor: TYPE_COLOR[type] }}
          />
          {TYPE_LABEL[type]}
        </span>
      ))}
      <span className="flex items-center gap-1.5">
        <span
          className="size-3 rounded-[3px] border-2"
          style={{ borderColor: TYPE_COLOR.VACATION }}
        />
        Awaiting approval
      </span>
      <span className="flex items-center gap-1.5">
        <span className="flex size-3 items-center justify-center rounded-[3px] border border-dashed border-muted-foreground/40 bg-muted/40 text-[7px] font-bold">
          H
        </span>
        Public holiday
      </span>
      <span className="flex items-center gap-1.5">
        <span className="size-3 rounded-[3px] bg-muted/60" />
        Not a working day
      </span>
    </div>
  );
}

/** A row worth drawing: any leave, requested or approved. */
function hasAnything(row: CalendarRow): boolean {
  return Object.values(row.days).some((mark) => mark.kind === 'absence');
}

const UTC = { timeZone: 'UTC' } as const;

/** Day-of-week for a day of the month, given which weekday the 1st was. 1 = Monday. */
function weekdayOf(firstWeekday: number, day: number): number {
  return ((firstWeekday - 1 + day - 1) % 7) + 1;
}

function weekdayInitial(firstWeekday: number, day: number): string {
  return ['M', 'T', 'W', 'T', 'F', 'S', 'S'][weekdayOf(firstWeekday, day) - 1]!;
}

function isWeekendColumn(firstWeekday: number, day: number): boolean {
  return weekdayOf(firstWeekday, day) >= 6;
}

function monthLabel(month: string): string {
  return new Date(`${month}-01T00:00:00.000Z`).toLocaleDateString('en-IN', {
    month: 'long',
    year: 'numeric',
    ...UTC,
  });
}

function shiftMonth(month: string, delta: number): string {
  const [year, m] = month.split('-').map(Number);
  return new Date(Date.UTC(year!, m! - 1 + delta, 1)).toISOString().slice(0, 7);
}
